// Date formatting utilities for DD-MM-YYYY format
const parseDateStringAsLocalDate = (dateString) => {
  if (!dateString) return null;

  if (dateString instanceof Date) {
    return dateString;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  if (/^\d{4}-\d{2}-\d{2}T/.test(dateString) && !/[zZ]|[+-]\d{2}:\d{2}$/.test(dateString)) {
    const [datePart] = dateString.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  return new Date(dateString);
};

export const formatDateForDisplay = (dateString) => {
  if (!dateString) return '';
  
  try {
    const date = parseDateStringAsLocalDate(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
};

export const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  
  try {
    // Convert DD-MM-YYYY to YYYY-MM-DD for HTML date input
    if (dateString.includes('-') && dateString.length === 10) {
      const parts = dateString.split('-');
      if (parts.length === 3) {
        // Check if it's already in YYYY-MM-DD format
        if (parts[0].length === 4) {
          return dateString; // Already in correct format
        }
        // Convert from DD-MM-YYYY to YYYY-MM-DD
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
    }
    
    const date = parseDateStringAsLocalDate(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('Error formatting date for input:', error);
    return '';
  }
};

export const formatDateForAPI = (dateString) => {
  if (!dateString) return '';
  
  try {
    // Convert YYYY-MM-DD (from HTML input) to DD-MM-YYYY for API
    const date = parseDateStringAsLocalDate(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch (error) {
    console.error('Error formatting date for API:', error);
    return dateString;
  }
};

export const parseAPIDate = (apiDateString) => {
  if (!apiDateString) return '';
  
  try {
    // Convert DD-MM-YYYY from API to YYYY-MM-DD for HTML input
    const parts = apiDateString.split('-');
    if (parts.length === 3) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return apiDateString;
  } catch (error) {
    console.error('Error parsing API date:', error);
    return apiDateString;
  }
};

export const getCurrentDateForInput = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const getCurrentDateForAPI = () => {
  const today = new Date();
  const day = String(today.getDate()).padStart(2, '0');
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const year = today.getFullYear();
  return `${day}-${month}-${year}`;
};

export const compareDateOnlyDesc = (firstDate, secondDate) => {
  const firstValue = formatDateForInput(firstDate);
  const secondValue = formatDateForInput(secondDate);

  if (firstValue === secondValue) {
    return 0;
  }

  if (!firstValue) {
    return 1;
  }

  if (!secondValue) {
    return -1;
  }

  return secondValue.localeCompare(firstValue);
};

export const calculateAgeFromDateString = (dateString) => {
  if (!dateString) return '';

  const birthDate = parseDateStringAsLocalDate(dateString);
  if (Number.isNaN(birthDate.getTime())) {
    return '';
  }

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

export const formatDateTimeForBrowser = (dateTimeString) => {
  if (!dateTimeString) return '';

  try {
    let date;

    if (/^\d{4}-\d{2}-\d{2}T/.test(dateTimeString) && !/[zZ]|[+-]\d{2}:\d{2}$/.test(dateTimeString)) {
      date = new Date(`${dateTimeString}Z`);
    } else {
      date = new Date(dateTimeString);
    }

    return new Intl.DateTimeFormat('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    }).format(date);
  } catch (error) {
    console.error('Error formatting date time for browser:', error);
    return dateTimeString;
  }
};
